// Shared data for the Booking Hub description prototypes
// (/service-entry-optionone, /service-entry-optionTwo, /service-entry-optionlast).
// Descriptions are the exact text from "Booking Hub Descriptions".
import { useSelector } from "react-redux";
import CoachingSessionIcon from "../icons/coaching-session.svg";
import CommunitySupportIcon from "../icons/community-support.svg";
import DemonstrationAudienceIcon from "../icons/demonstration-audience.svg";
import GroupCoachingIcon from "../icons/group-coaching.svg";
import HostSessionIcon from "../icons/host-session.svg";
import OnDemandPracticeIcon from "../icons/on-demand-practice.svg";
import OutreachClientIcon from "../icons/outreach-client.svg";
import PaidReadingIcon from "../icons/paid-reading.svg";
import PracticeCirclesIcon from "../icons/practice-circles.svg";
import PracticeDemonstrationIcon from "../icons/practice-demonstration.svg";
import ProfessionalDemonstrationIcon from "../icons/professional-demonstration.svg";
import ScheduledPracticeIcon from "../icons/scheduled-practice.svg";
import SpeakerEventsIcon from "../icons/speaker-events.svg";
import StarterClientIcon from "../icons/starter-client.svg";
import TechnologySupportIcon from "../icons/technology-support.svg";

const coaching30MinuteSession =
  process.env.REACT_APP_COACHING_30_Minute_Session || 64;
const coaching60MinuteSession = 92;
// "Support Sessions" subsession id - live: 89, staging: 86 (same name both places)
const supportSessionId = process.env.REACT_APP_SUPPORT_SESSION_ID || 86;

const practice30Url = "/booking?to=bookwithother&session_length=62&calendar=true";
const practice60Url = "/booking?to=bookwithother&session_length=26&calendar=true";
const coaching30Url = `/booking?to=coaching1on1&session_length=${coaching30MinuteSession}&calendar=true`;
const coaching60Url = `/booking?to=coaching1on1&session_length=${coaching60MinuteSession}&calendar=true`;

const setCoachBackUrl = (url) => () => localStorage.setItem("coachListBackUrl", url);

export const NO_DESCRIPTION = "Description coming soon.";
export const ALL_EVENTS_URL = "/go-on-demand-practice?to=platforGroupAll";

/*
 * Item fields:
 *  label, icon, description
 *  to / onClick     – destination of a single-action tile
 *  options          – split tiles (30 / 60 Min, Upcoming / Past): [{ label, to, onClick }]
 *  optionsTitle     – heading for the options in the Option C panel
 *  groupEvent       – shows the GROUP badge
 *  smallLabel       – smaller title for long names
 *  disabled         – locked tile
 */
export const serviceColumns = [
  {
    title: "Practice",
    items: [
      {
        label: "1 on 1 Practices",
        icon: ScheduledPracticeIcon,
        optionsTitle: "Choose a session length",
        options: [
          { label: "30 Min", to: practice30Url },
          { label: "60 Min", to: practice60Url },
        ],
        description:
          "A 1-on-1 Mediumship practice is an exchange where two developing mediums take turns practicing evidential mediumship and providing supportive feedback to each other.",
      },
      {
        label: "1 on 1 On Demand",
        icon: OnDemandPracticeIcon,
        to: "/go-on-demand-practice?to=ondemand",
        description:
          "A 1-on-1 On Demand practice is an exchange where two developing mediums take turns practicing evidential mediumship and providing supportive feedback to each other. Matching is available during the first 15 minutes hourly, 24/7.",
      },
      {
        label: "Practice Circles",
        icon: PracticeCirclesIcon,
        to: "/go-on-demand-practice?to=allpracticecircles",
        groupEvent: true,
        description:
          "A Group practice circle is a supportive group where developing mediums take turns practicing evidential mediumship in front of a small group to build confidence, and receive gentle feedback in a low-pressure environment. Everyone is encouraged—but not required—to take a turn.",
      },
      {
        label: "Practice Demos",
        icon: PracticeDemonstrationIcon,
        to: "/go-on-demand-practice?to=platformDemonstration",
        groupEvent: true,
        description:
          "A Practice demonstration is a short gallery practice (5-7 minutes) where developing mediums demonstrate evidential mediumship, build confidence, refine skills, and receive constructive feedback from others in a group setting.",
      },
    ],
  },
  {
    title: "Serve",
    items: [
      {
        label: "Host Any",
        icon: HostSessionIcon,
        to: "/availability",
        description:
          "Host Any Session Type means you create and host scheduled Zoom sessions available to your VerySoul level by selecting a session type and managing participants. If it's open to your level it will be available in your drop down list under Choose Session Name when creating a schedule.",
      },
      {
        label: "Pro Demos",
        icon: ProfessionalDemonstrationIcon,
        to: "/go-on-demand-practice?to=professionalDemonstrations",
        groupEvent: true,
        description:
          "A Professional demonstration is a formal group event where a VerySoul staff member introduces the event, then a working medium provides evidential communication, allowing the public audience to receive connections. Hosting available to the VerySoul Professional medium level.",
      },
      {
        label: "Starter On Demand",
        icon: StarterClientIcon,
        disabled: true,
        description: "",
      },
      {
        label: "Outreach On Demand",
        icon: OutreachClientIcon,
        to: "/go-on-demand-client",
        description:
          "An OutReach session on-demand is a free on-demand mediumship for OutReach clients, connecting them with an available working medium without scheduling ahead. Available to the VerySoul Outreach/Donation Medium level.",
      },
    ],
  },
  {
    title: "Strengthen",
    items: [
      {
        label: "1 on 1 Coaching",
        icon: CoachingSessionIcon,
        optionsTitle: "Choose a session length",
        options: [
          { label: "30 Min", to: coaching30Url, onClick: setCoachBackUrl(coaching30Url) },
          { label: "60 Min", to: coaching60Url, onClick: setCoachBackUrl(coaching60Url) },
        ],
        description:
          "A mediumship 1-on-1 coaching session (30 or 60 minutes) Bring mediumship questions, concerns, or experiences to receive personalized guidance and support from an experienced mentor. Coaching sessions are complimentary only when the coach has not set a coaching fee in their profile.",
      },
      {
        label: "Group Coaching",
        icon: GroupCoachingIcon,
        to: "/go-on-demand-practice?to=groupcoaching",
        groupEvent: true,
        description:
          "A group coaching session is a group Q&A with an experienced medium, offering guidance and shared learning around mediumship development. Not a practice environment.",
      },
      {
        label: "Community Support Groups",
        icon: CommunitySupportIcon,
        to: "/go-on-demand-practice?to=communitysupportgroups",
        groupEvent: true,
        smallLabel: true,
        description:
          "A mediumship community support group is a welcoming space for mediums of all levels to connect, share experiences, ask questions, and support one another.",
      },
      {
        label: "Paid Pro Reading",
        icon: PaidReadingIcon,
        to: "/booking?to=professionalsessions&auto_open_professional=true&calendar=true",
        onClick: () => {
          localStorage.removeItem("bookingFrom");
        },
        description:
          "A professional paid session Private paid session with a professional medium providing compassionate, evidential communication. Browse medium profiles and reviews to choose your medium. (Verysoul mediums are welcome to book paid sessions)",
      },
    ],
  },
  {
    title: "Observe",
    items: [
      {
        label: "Speaker Events",
        icon: SpeakerEventsIcon,
        groupEvent: true,
        optionsTitle: "Which events would you like to see?",
        options: [
          { label: "Upcoming", to: "/go-on-demand-practice?to=speakerEvents" },
          { label: "Past", to: "/go-on-demand-practice?to=speakerEventsPast" },
        ],
        description:
          "A speaker or training event is a live or recorded educational session where VerySoul mediums or staff share training and special topics to help participants expand their knowledge and skills.",
      },
      {
        label: "Practice Dem Audience",
        icon: DemonstrationAudienceIcon,
        to: "/go-on-demand-practice?to=platformDemonstration",
        groupEvent: true,
        smallLabel: true,
        description:
          "A practice demonstration (audience) is a short gallery practice (5-7 minutes) where developing mediums demonstrate evidential mediumship, build confidence, refine skills, and receive constructive feedback from others in a group setting.",
      },
      {
        label: "Pro Dem Audience",
        icon: ProfessionalDemonstrationIcon,
        to: "/go-on-demand-practice?to=professionalDemonstrations",
        groupEvent: true,
        description:
          "A professional demonstration (audience) is a formal group event where a VerySoul staff member introduces the event, then a working medium provides evidential communication, allowing the public audience to receive connections.",
      },
      {
        label: "Tech Support",
        icon: TechnologySupportIcon,
        to: `/booking?to=supportteam&session_length=${supportSessionId}&calendar=true`,
        description:
          "A tech-support Zoom appointment is a live Zoom call where you meet directly with a support specialist for troubleshooting, setup, or real-time technical help. For email support, support@verysoul.com.",
      },
    ],
  },
];

// Same level rule as the live /service-entry page.
export function useServiceColumns() {
  const { current_mediumship_level } = useSelector((state) => state.user.user);
  const canAccessClientOnDemand =
    current_mediumship_level == 3 ||
    current_mediumship_level == 5 ||
    current_mediumship_level == 6 ||
    current_mediumship_level == 7;

  return serviceColumns.map((column) => ({
    ...column,
    items: column.items.map((item) =>
      item.label === "Outreach On Demand" && !canAccessClientOnDemand
        ? { ...item, disabled: true, to: undefined }
        : item
    ),
  }));
}

export function tileClassName(item, extra = []) {
  return [
    "service-entry-tile",
    item.groupEvent ? "service-entry-tile--tagged" : "",
    item.disabled ? "service-entry-tile--disabled" : "",
    item.smallLabel ? "service-entry-tile--small" : "",
    ...extra,
  ]
    .filter(Boolean)
    .join(" ");
}
